/**
 * Utility functions for handling takeback verification
 */

/**
 * Check if a takeback has been verified in-store by an associate
 * @param {Object} takeback - The takeback object
 * @returns {boolean} - True if verified in-store, false otherwise
 */
export const isInStoreVerified = (takeback) => {
  if (!takeback) return false;

  // Check for verification status in status history
  const hasInStoreVerifiedStatus = takeback.status_history?.some(
    (status) => status.status === "in-store-verified"
  );

  // Check for verification timestamp in data
  const data =
    typeof takeback.data === "string"
      ? JSON.parse(takeback.data || "{}")
      : takeback.data || {};

  const hasInStoreVerifiedData = Boolean(data.inStoreVerifiedAt);

  return hasInStoreVerifiedStatus || hasInStoreVerifiedData;
};

/**
 * Check if a takeback has been verified through dropoff flow
 * @param {Object} takeback - The takeback object
 * @returns {boolean} - True if verified through dropoff, false otherwise
 */
export const isDropoffVerified = (takeback) => {
  if (!takeback) return false;

  return takeback.status_history?.some(
    (status) => status.status === "seller-dropoff-accepted"
  );
};

/**
 * Check if a takeback has been verified through warehouse processing
 * @param {Object} takeback - The takeback object
 * @returns {boolean} - True if verified through warehouse, false otherwise
 */
export const isWarehouseVerified = (takeback) => {
  if (!takeback) return false;

  return takeback.status_history?.some(
    (status) => status.status === "verified"
  );
};

/**
 * Get the verification type and details for a takeback
 * @param {Object} takeback - The takeback object
 * @returns {Object} - Verification details object
 */
export const getVerificationDetails = (takeback) => {
  if (!takeback) {
    return {
      isVerified: false,
      verificationType: null,
      verifiedAt: null,
      verifiedBy: null,
    };
  }

  const data =
    typeof takeback.data === "string"
      ? JSON.parse(takeback.data || "{}")
      : takeback.data || {};

  if (isInStoreVerified(takeback)) {
    return {
      isVerified: true,
      verificationType: "in-store",
      verifiedAt: data.inStoreVerifiedAt,
      verifiedBy: data.verified_by_associate,
    };
  }

  if (isDropoffVerified(takeback)) {
    const dropoffStatus = takeback.status_history?.find(
      (status) => status.status === "seller-dropoff-accepted"
    );
    return {
      isVerified: true,
      verificationType: "dropoff",
      verifiedAt: dropoffStatus?.updated_at,
      verifiedBy: "customer-self-service",
    };
  }

  if (isWarehouseVerified(takeback)) {
    const warehouseStatus = takeback.status_history?.find(
      (status) => status.status === "verified"
    );
    return {
      isVerified: true,
      verificationType: "warehouse",
      verifiedAt: warehouseStatus?.updated_at,
      verifiedBy: data.processed_by_user_id,
    };
  }

  return {
    isVerified: false,
    verificationType: null,
    verifiedAt: null,
    verifiedBy: null,
  };
};

/**
 * Create a verification status object for TES API
 * @param {string} type - Type of verification ("in-store", "dropoff", "warehouse", "associate")
 * @param {string} associateEmail - Email of the verifying associate (for in-store/associate)
 * @returns {Object} - Status object for TES API
 */
export const createVerificationStatus = (type) => {
  const statusMap = {
    "in-store": "in-store-verified",
    associate: "associate-verified",
    "seller-in-store": "seller-accepted",
    warehouse: "verified",
  };

  const parentStatusMap = {
    "in-store": "processing",
    associate: "processing",
    "seller-in-store": "processing",
    warehouse: "processed",
  };

  return {
    status: statusMap[type],
    parent_status: parentStatusMap[type],
    updated_at: parseInt(Date.now() / 1000),
  };
};

/**
 * Create verification data object for takeback update
 * @param {string} type - Type of verification ("in-store", "dropoff", "warehouse", "associate")
 * @param {Object} options - Additional options
 * @returns {Object} - Data object for takeback update
 */
export const createVerificationData = (type, options = {}) => {
  const { weight, paymentType, associateEmail, receivedAt, acceptedTerms } =
    options;

  const baseData = {
    ...(weight && {
      u_lego_postsort_weight: parseFloat(weight),
      u_lego_payment_weight: parseFloat(weight),
    }),
    ...(paymentType && { paymentType }),
    ...(receivedAt && { receivedAt }),
    ...(acceptedTerms && { u_accepted_terms: true }),
  };

  switch (type) {
    case "in-store":
      return {
        ...baseData,
        inStoreVerifiedAt: new Date().toISOString(),
        verified_by_associate: associateEmail,
      };

    case "associate":
      return {
        ...baseData,
        associateVerifiedAt: new Date().toISOString(),
        verified_by_associate: associateEmail,
      };

    case "dropoff":
      return {
        ...baseData,
        dropoffVerifiedAt: new Date().toISOString(),
      };

    case "warehouse":
      return {
        ...baseData,
        warehouseVerifiedAt: new Date().toISOString(),
      };

    default:
      return baseData;
  }
};
